import type { SessionUser } from './api';
import type { WardrobeItem, WardrobeItemInput } from '../../shared/wardrobe';

const DATABASE_NAME = 'her-closet-private-cache';
const DATABASE_VERSION = 1;
const SNAPSHOT_STORE = 'snapshots';
const PHOTO_STORE = 'photos';
const DRAFT_STORE = 'drafts';
const USER_KEY = 'her_closet_offline_user';
const ACTIVE_ITEMS_KEY = 'active-items';
const UPLOAD_DRAFT_KEY = 'manual-upload';

export interface UploadDraft {
  input: WardrobeItemInput;
  photo: Blob;
  photoName: string;
  photoType: string;
  requestKey: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onerror = () => reject(request.error ?? new Error('Private device storage failed.'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(SNAPSHOT_STORE)) {
        database.createObjectStore(SNAPSHOT_STORE);
      }
      if (!database.objectStoreNames.contains(PHOTO_STORE)) {
        database.createObjectStore(PHOTO_STORE);
      }
      if (!database.objectStoreNames.contains(DRAFT_STORE)) {
        database.createObjectStore(DRAFT_STORE);
      }
    };
  });
}

async function storeValue(storeName: string, key: string, value: unknown): Promise<void> {
  if (!('indexedDB' in globalThis)) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    transaction.objectStore(storeName).put(value, key);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('Private device storage failed.'));
  });
  database.close();
}

async function readValue<T>(storeName: string, key: string): Promise<T | null> {
  if (!('indexedDB' in globalThis)) return null;
  const database = await openDatabase();
  const value = await new Promise<T | null>((resolve, reject) => {
    const request = database.transaction(storeName, 'readonly').objectStore(storeName).get(key);
    request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('Private device storage failed.'));
  });
  database.close();
  return value;
}

export function rememberOfflineUser(user: SessionUser): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // Offline browsing remains optional when device storage is unavailable.
  }
}

export function readOfflineUser(): SessionUser | null {
  try {
    const value = localStorage.getItem(USER_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<SessionUser>;
    return parsed.id && parsed.username && parsed.displayName ? (parsed as SessionUser) : null;
  } catch {
    return null;
  }
}

export async function cacheWardrobeSnapshot(items: WardrobeItem[]): Promise<void> {
  try {
    await storeValue(SNAPSHOT_STORE, ACTIVE_ITEMS_KEY, items);
    if (!navigator.serviceWorker?.controller) return;
    const remaining = [...items];
    const workers = Array.from({ length: Math.min(4, remaining.length) }, async () => {
      while (remaining.length) {
        const item = remaining.shift();
        if (!item) return;
        const response = await fetch(item.photoUrl, {
          credentials: 'include',
          headers: { Accept: 'image/*' },
        });
        if (!response.ok) continue;
        const photo = await response.blob();
        if (photo.size <= 3 * 1024 * 1024) {
          await storeValue(PHOTO_STORE, item.id, photo);
        }
      }
    });
    await Promise.all(workers);
  } catch {
    // A cache failure never blocks the online closet.
  }
}

export async function readWardrobeSnapshot(): Promise<WardrobeItem[]> {
  const items = (await readValue<WardrobeItem[]>(SNAPSHOT_STORE, ACTIVE_ITEMS_KEY)) ?? [];
  return Promise.all(
    items.map(async (item) => {
      const photo = await readValue<Blob>(PHOTO_STORE, item.id);
      return {
        ...item,
        photoUrl:
          photo && typeof URL.createObjectURL === 'function'
            ? URL.createObjectURL(photo)
            : item.photoUrl,
      };
    }),
  );
}

export async function saveUploadDraft(draft: UploadDraft): Promise<void> {
  await storeValue(DRAFT_STORE, UPLOAD_DRAFT_KEY, draft);
}

export async function readUploadDraft(): Promise<UploadDraft | null> {
  return readValue<UploadDraft>(DRAFT_STORE, UPLOAD_DRAFT_KEY);
}

export async function clearUploadDraft(): Promise<void> {
  if (!('indexedDB' in globalThis)) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(DRAFT_STORE, 'readwrite');
    transaction.objectStore(DRAFT_STORE).delete(UPLOAD_DRAFT_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('Private device storage failed.'));
  });
  database.close();
}

export async function clearPrivateDeviceData(): Promise<void> {
  try {
    localStorage.removeItem(USER_KEY);
  } catch {
    // Continue with IndexedDB cleanup.
  }
  if (!('indexedDB' in globalThis)) return;
  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}
